import {useRef, useState} from "react";

export type ResizeAxis = 'x' | 'y';

interface UseResizableOptions {
    // localStorage key the size persists under.
    storageKey: string;
    // 'x' → a vertical bar dragged horizontally (controls a width).
    // 'y' → a horizontal bar dragged vertically (controls a height).
    axis: ResizeAxis;
    min: number;
    // Initial size when nothing valid is stored. A thunk lets it be computed
    // (e.g. viewport-relative) without running every render.
    initial: number | (() => number);
    // Optional dynamic upper bound, evaluated on each drag move so it can read
    // a container ref and clamp against the space actually available.
    max?: () => number;
    // Set when the resized panel sits *after* the bar: dragging toward the bar
    // (a negative axis delta) should grow it, so the delta sign is flipped.
    invert?: boolean;
}

export interface ResizeHandleProps {
    onPointerDown(e: React.PointerEvent<HTMLDivElement>): void;
    onPointerMove(e: React.PointerEvent<HTMLDivElement>): void;
    onPointerUp(): void;
    onPointerCancel(): void;
}

// Persisted, pointer-captured resize state for one draggable bar. Returns the
// current size plus the handlers to spread onto a <ResizeBar/>. Pointer capture
// keeps the drag tracking even when the cursor leaves the thin handle.
export function useResizable(opts: UseResizableOptions): {size: number; setSize(v: number): void; handleProps: ResizeHandleProps} {
    const {storageKey, axis, min} = opts;
    const [size, _setSize] = useState<number>(() => {
        const stored = Number(localStorage.getItem(storageKey));
        if (Number.isFinite(stored) && stored >= min) return stored;
        return typeof opts.initial === 'function' ? opts.initial() : opts.initial;
    });
    function setSize(v: number) {
        localStorage.setItem(storageKey, String(v));
        _setSize(v);
    }
    const dragRef = useRef<{start: number; startSize: number} | null>(null);
    function clamp(raw: number): number {
        const max = opts.max ? Math.max(min, opts.max()) : Number.POSITIVE_INFINITY;
        return Math.round(Math.min(max, Math.max(min, raw)));
    }
    const handleProps: ResizeHandleProps = {
        onPointerDown(e) {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            dragRef.current = {start: axis === 'x' ? e.clientX : e.clientY, startSize: size};
        },
        onPointerMove(e) {
            const drag = dragRef.current;
            if (!drag || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
            const delta = (axis === 'x' ? e.clientX : e.clientY) - drag.start;
            setSize(clamp(opts.invert ? drag.startSize - delta : drag.startSize + delta));
        },
        onPointerUp() { dragRef.current = null; },
        onPointerCancel() { dragRef.current = null; },
    };
    return {size, setSize, handleProps};
}

// The bar itself: an 8px hit strip with a centered grip. `axis` selects the
// cursor and orientation; spread a useResizable() handleProps onto it.
function ResizeBar(props: {axis: ResizeAxis} & ResizeHandleProps) {
    const {axis, ...handlers} = props;
    const vertical = axis === 'x';   // a vertical bar, dragged horizontally
    return (
        <div className={'layout center'} title={'Drag to resize'} {...handlers}
             style={{
                 flexShrink: 0, touchAction: 'none',
                 ...(vertical
                     ? {width: 8, cursor: 'col-resize', margin: '0 2px'}
                     : {height: 8, cursor: 'row-resize', margin: '2px 0'}),
             }}>
            <div style={{
                borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.25)', pointerEvents: 'none',
                ...(vertical ? {width: 3, height: 60} : {height: 3, width: 60}),
            }}/>
        </div>
    );
}

export default ResizeBar;
